import React from 'react';
import { DynamicContainer, Form, FormSection } from '@hai/ui-react';
import { useTranslation } from 'react-i18next';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { Service } from 'src/js/model/service';
import { ServicesViewModel } from 'src/js/pages/settings/services/services-view-model';
import { FormChangeEvent } from 'src/js/util/global-type';
import { NicOptions } from './services';
import { generateDataAuto } from 'src/js/util/automation';
import { ServiceToggle } from './service-toggle';
import { FormSwitch } from 'src/js/component/base/form/form-switch';
import { removeItemFromArray } from 'src/js/util/global-util';

interface Props {
  services: ServicesViewModel;
  onServiceChange: (service: Service, value: boolean) => Promise<any> | void;
  onServiceNicChange: (event: FormChangeEvent) => void;
  nicOptions: NicOptions;
  comPortDisabled: boolean;
  loading: boolean;
}

export const GeneralServices = (props: Props) => {
  const { services, onServiceChange, onServiceNicChange, nicOptions, comPortDisabled, loading } = props;
  const { t } = useTranslation();
  const renderServiceLabel = (service: { key: string }) => t(`settings.services.section.general.${service.key}`);
  const servicesList = [
    { key: Service.EMS },
    { key: Service.HTTP },
    { key: Service.NMOS, condition: systemInfo.personality === '2110' },
    { key: Service.ONVIF, condition: systemInfo.onvif },
    { key: Service.PASSTHROUGH, disabled: comPortDisabled },
    { key: Service.RTSP },
    { key: Service.SNMP },
    { key: Service.SSH },
    { key: Service.TELNET },
  ];

  let servicesListCleaned = servicesList;
  //Clean non-applicable servces
  if (systemInfo.personality !== '2110') {
    servicesListCleaned = removeItemFromArray(servicesList, (service) => service.key === 'nmos');
  }
  if (!systemInfo.onvif) {
    servicesListCleaned = removeItemFromArray(servicesList, (service) => service.key === 'onvif');
  }
  if (!systemInfoCapabilities.hasComPort()) {
    servicesListCleaned = removeItemFromArray(servicesList, (service) => service.key === 'passthrough');
  }

  return (
    <Form className="hai-mb-6">
      <FormSection
        title={t('settings.services.section.general.title')}
        data-auto={generateDataAuto('form', 'section general')}
      >
        {systemInfo.hasEth1 ? (
          <>
            {servicesListCleaned.map((service) => {
              const serviceKey = service.key as keyof ServicesViewModel;
              const nicKey = service.key as keyof typeof services.nic;
              return (
                <ServiceToggle
                  key={service.key}
                  serviceKey={service.key}
                  serviceLabel={renderServiceLabel(service)}
                  dropdownLabel={t('settings.services.section.general.interface')}
                  checked={!!services[serviceKey]}
                  loading={loading}
                  nicValue={t(`settings.network.options.${services.nic[nicKey] || 'all'}`)}
                  nicOptions={nicOptions}
                  onServiceChange={(checked: boolean) => onServiceChange(service.key, checked)}
                  onServiceNicChange={onServiceNicChange}
                  disabled={service.disabled === comPortDisabled ? comPortDisabled : false}
                />
              );
            })}
          </>
        ) : (
          <>
            <DynamicContainer minColumns={6} maxColumns={6}>
              {servicesListCleaned.map((service) => {
                return (
                  <FormSwitch
                    key={service.key}
                    checked={!!services[service.key as keyof ServicesViewModel]}
                    loading={loading}
                    switchLabel={renderServiceLabel(service)}
                    onChange={(checked: boolean) => onServiceChange(service.key, checked)}
                    disabled={service.disabled === comPortDisabled ? comPortDisabled : false}
                  />
                );
              })}
            </DynamicContainer>
          </>
        )}
      </FormSection>
    </Form>
  );
};
